import React from "react";
import { Box } from "@mui/material";
import Section from "../ui/Section";
import Figure from "../ui/Figure";
import Reveal from "../ui/Reveal";
import Button, { ArrowLink } from "../ui/Button";
import { Accent, Display, Label } from "../ui/Type";
import { color, font, motion, type } from "../../design/tokens";

/**
 * A run of projects, each in a different composition.
 *
 * The rule this section exists to break: a three-column grid of identical
 * cards, which flattens eight different jobs into one texture. Instead the
 * layout cycles through four compositions — wide split, offset pair, tall
 * portrait with the copy beside it, and a two-up — so the eye has to
 * re-engage with each entry.
 *
 * `layout` is derived from position, not stored on the project, so adding a
 * project to the data file slots it into the rhythm without anyone having to
 * pick a layout for it.
 */

const ProjectEntry = ({ project, index }) => {
  const layout = index % 4;
  const [lead, second] = project.images;

  const Meta = ({ tone = "ink" }) => (
    <Box>
      <Box
        component="p"
        sx={{ ...type.label, color: color.clay, margin: 0 }}
      >
        {project.category}
      </Box>
      <Display as="h3" size="md" tone={tone} sx={{ marginTop: 2 }}>
        {project.title}
      </Display>
      <Box
        component="p"
        sx={{ ...type.body, color: color.graphite, marginTop: 2.5, maxWidth: 520 }}
      >
        {project.summary}
      </Box>
      {project.serviceSlug ? (
        <Box sx={{ marginTop: 3 }}>
          <ArrowLink to={`/services/${project.serviceSlug}`}>
            {project.category} services
          </ArrowLink>
        </Box>
      ) : null}
    </Box>
  );

  // ── Wide split: big plate one side, copy the other ──────────────────────
  if (layout === 0) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
          gap: { xs: 4, md: 8 },
          alignItems: "center",
        }}
      >
        <Figure media={lead} ratio={{ xs: "4 / 5", md: "5 / 4" }} />
        <Meta />
      </Box>
    );
  }

  // ── Offset pair: two plates at different heights, copy below ────────────
  if (layout === 1) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.8fr 1.2fr" },
          gap: { xs: 4, md: 8 },
          alignItems: "start",
        }}
      >
        <Box sx={{ order: { md: 2 } }}>
          <Figure media={lead} ratio="4 / 5" />
        </Box>
        <Box sx={{ order: { md: 1 }, paddingTop: { md: 9 } }}>
          <Meta />
          {second ? (
            <Box sx={{ marginTop: 5, maxWidth: { md: 280 } }}>
              <Figure media={second} ratio="1 / 1" />
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  // ── Tall portrait, copy in a narrow column beside it ────────────────────
  if (layout === 2) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
          gap: { xs: 4, md: 10 },
          alignItems: "end",
        }}
      >
        <Figure media={lead} ratio="4 / 5" />
        <Box sx={{ paddingBottom: { md: 4 } }}>
          <Meta />
        </Box>
      </Box>
    );
  }

  // ── Two-up: copy above, plates side by side ─────────────────────────────
  return (
    <Box>
      <Box sx={{ maxWidth: 720 }}>
        <Meta />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: second ? "repeat(2, 1fr)" : "1fr" },
          gap: { xs: 3, md: 5 },
          marginTop: { xs: 4, md: 6 },
        }}
      >
        <Figure media={lead} ratio="1 / 1" />
        {second ? <Figure media={second} ratio="1 / 1" /> : null}
      </Box>
    </Box>
  );
};

const ProjectCollection = ({
  projects = [],
  eyebrow = "Selected work",
  title,
  intro,
  tone = "paper",
  showCta = true,
  limit,
}) => {
  const shown = limit ? projects.slice(0, limit) : projects;
  if (!shown.length) return null;

  return (
    <Section tone={tone} id="projects">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 3, md: 8 },
          alignItems: "end",
          marginBottom: { xs: 6, md: 10 },
        }}
      >
        <Box>
          <Label>{eyebrow}</Label>
          <Display as="h2" size="lg" sx={{ marginTop: 3 }}>
            {title || (
              <>
                Real jobs, <Accent>real photos</Accent>.
              </>
            )}
          </Display>
        </Box>
        <Box component="p" sx={{ ...type.body, color: color.graphite, margin: 0, maxWidth: 460 }}>
          {intro ||
            "Every photograph on this site is one of our own job sites — some finished, some mid-build. Nothing here is stock, and the captions say plainly what each one shows."}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: { xs: 8, md: 14 } }}>
        {shown.map((project, index) => (
          <Reveal key={project.id}>
            <ProjectEntry project={project} index={index} />
          </Reveal>
        ))}
      </Box>

      {showCta ? (
        <Box
          sx={{
            marginTop: { xs: 8, md: 12 },
            paddingTop: 5,
            borderTop: `1px solid ${color.line}`,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          <Box
            component="p"
            sx={{
              fontFamily: font.display,
              fontSize: "clamp(1.35rem, 2.4vw, 1.9rem)",
              lineHeight: 1.25,
              color: color.ink,
              margin: 0,
              maxWidth: 520,
              transition: `color ${motion.base} ${motion.ease}`,
            }}
          >
            Planning something similar? Tell us what you're working with.
          </Box>
          <Button to="/contact" variant="primary" size="lg">
            Get a Free Quote
          </Button>
        </Box>
      ) : null}
    </Section>
  );
};

export default ProjectCollection;
